<!-- Component can be used in microscope and periscope -->
<template>
  <LabelAndHint
    v-slot="{ inputId, ariaLabelledby, isSuccess, isWarning, isErr }"
    v-bind="{ ...props, error: props.error || invalid, errorMessages: props.errorMessages || errorMessages }"
  >
    <div
      class="select-wrapper"
      :class="{
        'input-success': isSuccess,
        'input-warning': isWarning,
        'input-error': isErr,
        'input-disabled': disabled,
        borderless: borderless,
        slim: slim,
      }"
    >
      <select
        :id="inputId"
        ref="selectElement"
        :disabled="props.disabled"
        :value="currentValue"
        :aria-labelledby="ariaLabelledby"
        :class="{ disabled: disabled, 'placeholder-color': !currentValue }"
        @focus="emit('focus')"
        @blur="onBlur"
        @input="onInput"
      >
        <option :disabled="mandatory" class="disabled" value="" :selected="!currentValue">
          {{ props.placeholder ?? 'Välj ett alternativ' }}
        </option>
        <option
          v-for="(option, index) in options"
          :key="`${index}-${option.value}`"
          :value="option.value"
          :selected="option.value === currentValue"
          :disabled="option.disabled"
        >
          {{ option.text }}
        </option>
      </select>
      <ChevronDown class="custom-select-icon" />
    </div>
  </LabelAndHint>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'

import type { ValidationRuleObject } from '@staff/shared'

import ChevronDown from '../../icons/IconChevronDown.vue'
import { useInputValidation } from '../../composables'
import LabelAndHint from '../AppTextField/LabelAndHint.vue'

const props = defineProps<{
  id?: string | number

  // For shared components to work in both vue 2.7 and vue 3 we need to support both `value` and `modelValue`
  // When periscope is updated to vue 3, we can remove `value` and only use `modelValue`
  value?: string | null
  modelValue?: string | null

  options: { text: string; value: string; disabled?: boolean }[]
  mandatory?: boolean

  borderless?: boolean
  slim?: boolean
  label?: string
  hint?: string | string[]
  hideDetails?: boolean
  placeholder?: string

  success?: boolean
  successMessages?: string | string[]

  warning?: boolean
  warningMessages?: string | string[]

  error?: boolean
  errorMessages?: string | string[]

  disabled?: boolean

  rules?: (ValidationRuleObject | null)[]
  validationOptions?: {
    validateOnModelChange?: boolean
  }
}>()

const emit = defineEmits<{
  (e: 'input', v: string): void
  (e: 'update:modelValue', v: string): void
  (e: 'focus'): void
  (e: 'blur'): void
}>()

// For vue 2.7 and vue 3 compatibility
const currentValue = computed(() => props.modelValue ?? props.value)

const selectElement = ref<HTMLSelectElement | null | undefined>(undefined)

const { invalid, errorMessages, validate } = useInputValidation(
  () => currentValue.value,
  () => props.rules,
  selectElement,
)

const onInput = (event: Event) => {
  if (event.target instanceof HTMLSelectElement) {
    emit('input', event.target.value)
    emit('update:modelValue', event.target.value)
  }

  if ((props.rules?.length ?? 0) > 0) {
    nextTick(() => validate())
  }
}

const onBlur = () => {
  emit('blur')

  if ((props.rules?.length ?? 0) > 0) {
    nextTick(() => validate())
  }
}

watch(
  () => currentValue.value,
  () => {
    if (invalid.value || props.validationOptions?.validateOnModelChange) {
      nextTick(() => validate())
    }
  },
)
</script>

<style lang="scss" scoped>
.select-wrapper {
  position: relative;

  border: 1px solid var(--color-border-default);
  border-radius: 6px;

  &:focus-within {
    border-color: var(--color-accent-fg);
    outline: 2px solid var(--color-accent-fg);
    outline-offset: -2px;

    &.input-error {
      outline: 2px solid var(--color-danger-fg);
    }
  }

  &.borderless {
    border: none;
    outline: none;
  }

  &.slim {
    select {
      padding: 1px 10px 1px 5px;
      font-size: 12px;
      border-radius: 3px;
    }
    .custom-select-icon {
      position: absolute;
      display: flex;
      align-items: center;
      top: 50%;
      right: 2px;
      transform: translateY(-50%);
      pointer-events: none;
      width: 12px;
    }
  }

  &.input-success {
    border-color: var(--color-textfield-success-border);
  }

  &.input-warning {
    border-color: var(--color-textfield-warning-border);
  }

  &.input-error {
    border-color: var(--color-danger-fg);
  }
}

.custom-select-icon {
  position: absolute;
  display: flex;
  align-items: center;
  top: 50%;
  right: 8px;
  transform: translateY(-50%);
  pointer-events: none;
}

label {
  width: 100%;
  display: inline-block;
  margin-bottom: 4px;
  font-size: 14px;

  &.disabled {
    color: var(--color-fg-disabled);
  }
}

select {
  appearance: none;
  -webkit-appearance: none;

  display: block;
  width: 100%;
  padding: 6px 34px 6px 12px;

  color: var(--color-fg-default);
  font-size: 16px;

  background-color: transparent;
  border: none;

  cursor: pointer;
  overflow: hidden;
  text-overflow: ellipsis;

  &:focus,
  &:hover {
    border-color: var(--color-border-default);
  }

  &:focus-visible {
    outline: none;
  }
  &.placeholder-color {
    color: var(--color-fg-disabled);
  }
  &.disabled {
    cursor: not-allowed;
    border-color: var(--color-textfield-disabled-border);

    &::placeholder {
      color: var(--color-fg-disabled);
    }
  }
}
</style>
